/** @format */

import {
  CheckOutlined,
  ClockCircleOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Alert, Button, Input, Segmented, Select, Skeleton, Spin } from "antd";
import moment from "moment";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  TChiCe,
  TChiCePersona,
  TChiCeTurno,
} from "../../../../redux-modules/declarations/maps/home";
import { TLettura } from "../../ViewPresenze/usePresenze";
import { AvatarPersona } from "../../ViewPresenze/partials/AvatarPersona";
import { leggiPreferenzeHome, normalizza, orario, salvaPreferenzeHome } from "../format";

export type TMomento = "adesso" | "oggi" | "domani";

interface Props {
  lettura: TLettura<TChiCe>;
  momento: TMomento;
  onMomento: (m: TMomento) => void;
  gruppo: string;
  onGruppo: (g: string) => void;
  /** Group/Canswitchpresencetoday: si sceglie tra tutti i gruppi visibili, altrimenti solo i propri */
  cambiaGruppo: boolean;
  gruppiUtente: string[];
  utenteId: string;
  tipoStraordinarioId: number | null;
  /** "YYYY-MM-DD HH:mm" del browser */
  adesso: string;
}

/** Quante persone si vedono in una tessera; le altre si raggiungono scorrendo l'elenco. */
const PERSONE_VISIBILI = 6;

const MANCATA_TIMBRATURA = "Mancata timbratura";

/** Etichette brevi delle anomalie di whos-there; il testo completo resta nel title. */
const ANOMALIE_BREVI: Record<string, string> = {
  "Mancata timbratura": "Non timbrato",
  "Ingresso in ritardo": "Ritardo",
  "Ingresso in anticipo": "Anticipo",
  "Uscita in anticipo": "Uscita anticipata",
  "Uscita in ritardo": "Uscita in ritardo",
  "Turno troppo lungo": "Turno lungo",
  "Presenze incomplete": "Incompleta",
};

type TStatoTurno = "in_corso" | "timbrato" | "da_svolgere" | "non_timbrato" | "straordinario";

/** Un turno (o una presenza senza turno) della persona, come chip sotto il nome. */
interface TTurnoVisto {
  chiave: string;
  stato: TStatoTurno;
  /** "08:00–13:30", "dalle 08:24", "Straordinario" */
  testo: string;
  /** dopo il testo: "dalle 08:24", "non timbrato", "turno 08:00–13:30" */
  nota: string | null;
  /** anomalia diversa dalla mancata timbratura, in breve: "Ritardo" */
  avviso: string | null;
  avvisoCompleto: string | null;
  /** descrizione intera per title e lettori di schermo */
  descrizione: string;
  /** presenza ancora aperta: punto vivo al posto dell'icona */
  viva: boolean;
  ordine: string;
}

interface TPersonaVista {
  id: number;
  nome: string;
  cognome: string;
  turni: TTurnoVisto[];
  straordinario: boolean;
  anomalia: string | null;
  inServizio: boolean;
}

interface TRepartoVista {
  chiave: string;
  nome: string;
  membri: number | null;
  persone: TPersonaVista[];
}

const inizioTurno = (t: TChiCeTurno): string | null =>
  t.shift_type && t.date ? `${t.date} ${t.shift_type.time_from.substr(0, 5)}` : null;

const DESCRIZIONI: Record<TStatoTurno, string> = {
  in_corso: "In servizio",
  timbrato: "Timbrato",
  da_svolgere: "Da svolgere",
  non_timbrato: "Non timbrato",
  straordinario: "Straordinario",
};

/** Lo stato detto a voce, dove il testo del chip non lo dice già. */
const STATO_DA_LEGGERE: Partial<Record<TStatoTurno, string>> = {
  in_corso: "In servizio",
  timbrato: "Timbrato",
  da_svolgere: "Da svolgere",
};

/** Stato e testi di un turno di whos-there, secondo il momento scelto. */
const turnoVisto = (
  t: TChiCeTurno,
  indice: number,
  momento: TMomento,
  tipoStraordinarioId: number | null,
): TTurnoVisto => {
  const st = t.shift_type;
  const orarioTurno = st ? orario(st.time_from.substr(0, 5), st.time_to.substr(0, 5)) : null;
  const p = t.presence;
  const entrata = p ? p.datetime_from.substr(11, 5) : null;
  const uscita = p && p.datetime_to ? p.datetime_to.substr(11, 5) : null;
  const aperta = !!p && !p.datetime_to;
  const straordinario =
    !!p &&
    (t.shift_planning_id === null ||
      (tipoStraordinarioId !== null && Number(p.presence_type_id) === tipoStraordinarioId));
  const avvisoCompleto = t.anomaly && t.anomaly !== MANCATA_TIMBRATURA ? t.anomaly : null;

  let stato: TStatoTurno;
  let testo: string;
  let nota: string | null = null;
  if (straordinario) {
    stato = "straordinario";
    testo = "Straordinario";
    nota = aperta ? `dalle ${entrata}` : `${entrata}–${uscita}`;
  } else if (aperta) {
    stato = "in_corso";
    if (momento === "adesso" || !orarioTurno) {
      testo = `dalle ${entrata}`;
      nota = orarioTurno ? `turno ${orarioTurno}` : null;
    } else {
      testo = orarioTurno;
      nota = `dalle ${entrata}`;
    }
  } else if (p) {
    stato = "timbrato";
    if (momento === "adesso" && orarioTurno) {
      // turno ancora in corso, ma la persona ha già chiuso la timbratura
      testo = orarioTurno;
      nota = `uscito alle ${uscita}`;
    } else {
      testo = orarioTurno || `${entrata}–${uscita}`;
    }
  } else if (t.anomaly === MANCATA_TIMBRATURA) {
    stato = "non_timbrato";
    testo = orarioTurno || "Turno";
    nota = "non timbrato";
  } else {
    stato = "da_svolgere";
    testo = orarioTurno || "Turno";
  }

  const parti = [DESCRIZIONI[stato]];
  if (orarioTurno) parti.push(`turno ${orarioTurno}`);
  if (entrata) parti.push(uscita ? `timbrato ${entrata}–${uscita}` : `entrato alle ${entrata}`);
  if (t.office && t.office.name) parti.push(t.office.name);
  if (avvisoCompleto) parti.push(avvisoCompleto);

  return {
    chiave: `${t.shift_planning_id ?? "p"}-${p ? p.id : indice}`,
    stato,
    testo,
    nota,
    avviso: avvisoCompleto ? ANOMALIE_BREVI[avvisoCompleto] || avvisoCompleto : null,
    avvisoCompleto,
    descrizione: parti.join(", "),
    viva: aperta,
    ordine: (p ? p.datetime_from.substr(0, 16) : inizioTurno(t)) || "",
  };
};

/** Una persona come la mostra la tessera: un chip per ogni turno, secondo il momento scelto. */
const personaVista = (
  p: TChiCePersona,
  momento: TMomento,
  adesso: string,
  tipoStraordinarioId: number | null,
): TPersonaVista => {
  // "Mancata timbratura" su un turno non ancora iniziato non è un'anomalia (whos-there la dà per
  // ogni turno senza presenza): si mostra solo da quando il turno è cominciato
  const shifts = p.shifts.map(t => {
    const inizio = inizioTurno(t);
    return t.anomaly === MANCATA_TIMBRATURA && inizio && inizio > adesso
      ? { ...t, anomaly: null }
      : t;
  });
  const turni = shifts
    .map((t, i) => turnoVisto(t, i, momento, tipoStraordinarioId))
    .sort((a, b) => a.ordine.localeCompare(b.ordine));

  return {
    id: p.id,
    nome: p.name,
    cognome: p.surname,
    turni,
    straordinario: turni.some(t => t.stato === "straordinario"),
    anomalia: shifts.map(t => t.anomaly).find(a => !!a) || null,
    inServizio: shifts.some(t => !!t.presence && !t.presence.datetime_to),
  };
};

/** Icona dello stato: il colore non basta da solo. */
const IconaStato = ({ stato, viva }: { stato: TStatoTurno; viva: boolean }): JSX.Element | null => {
  if (viva) return <span className="punto" aria-hidden="true" />;
  if (stato === "timbrato" || stato === "straordinario")
    return <CheckOutlined aria-hidden="true" />;
  if (stato === "non_timbrato") return <ExclamationCircleOutlined aria-hidden="true" />;
  if (stato === "da_svolgere") return <ClockCircleOutlined aria-hidden="true" />;
  return null;
};

/**
 * Elenco delle persone di un reparto: le prime PERSONE_VISIBILI a vista, le altre scorrendo
 * dentro la tessera. L'altezza si misura sulle righe vere (un nome lungo o un turno spezzato
 * occupano due righe), con un'ombra in basso finché c'è altro da vedere.
 */
const ElencoScorrevole = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const elenco = useRef<HTMLUListElement>(null);
  const [altezza, setAltezza] = useState<number | null>(null);
  const [altro, setAltro] = useState(false);

  const controllaFine = (): void => {
    const ul = elenco.current;
    if (!ul) return;
    const resta = ul.scrollHeight - ul.scrollTop - ul.clientHeight > 2;
    if (resta !== altro) setAltro(resta);
  };

  useLayoutEffect(() => {
    const misura = (): void => {
      const ul = elenco.current;
      if (!ul) return;
      const voci = ul.children;
      let nuova: number | null = null;
      if (voci.length > PERSONE_VISIBILI) {
        const ultima = voci[PERSONE_VISIBILI - 1] as HTMLElement;
        nuova = ultima.offsetTop + ultima.offsetHeight;
      }
      if (nuova !== altezza) setAltezza(nuova);
      controllaFine();
    };
    misura();
    window.addEventListener("resize", misura);
    return (): void => window.removeEventListener("resize", misura);
  });

  return (
    <>
      <div className={`scorrevole${altro ? " altro" : ""}`}>
        <ul
          ref={elenco}
          className="persone"
          style={altezza !== null ? { maxHeight: altezza } : undefined}
          onScroll={controllaFine}
          tabIndex={altezza !== null ? 0 : undefined}
        >
          {children}
        </ul>
      </div>
      {altezza !== null && (
        <span className="suggerimento">
          {React.Children.count(children)} persone, scorri l'elenco per vederle tutte
        </span>
      )}
    </>
  );
};

const confrontaPersone = (a: TPersonaVista, b: TPersonaVista): number =>
  a.cognome.localeCompare(b.cognome, "it", { sensitivity: "base" }) ||
  a.nome.localeCompare(b.nome, "it", { sensitivity: "base" }) ||
  a.id - b.id;

/** "Chi c'è": GET /shift-plannings/whos-there, raggruppato per reparto anche su più gruppi. */
export const ChiCe = ({
  lettura,
  momento,
  onMomento,
  gruppo,
  onGruppo,
  cambiaGruppo,
  gruppiUtente,
  utenteId,
  tipoStraordinarioId,
  adesso,
}: Props): JSX.Element => {
  const { dati, caricamento, aggiornamento, errore, ricarica } = lettura;
  const [ricerca, setRicerca] = useState("");
  const [filtro, setFiltro] = useState<"tutti" | "straordinario" | "anomalie">("tutti");
  // tessere chiuse, ricordate nel browser come il gruppo scelto
  const [chiusi, setChiusi] = useState<string[]>(() => leggiPreferenzeHome().chiusi);
  const apriChiudi = (chiave: string): void => {
    const nuovi =
      chiusi.indexOf(chiave) >= 0 ? chiusi.filter(c => c !== chiave) : [...chiusi, chiave];
    setChiusi(nuovi);
    salvaPreferenzeHome({ chiusi: nuovi });
  };

  // la risposta deve essere quella del momento scelto: "Adesso" ha l'ora, "Oggi" e "Domani" no.
  // Mentre arriva quella nuova si mostra il caricamento, non i dati della scheda precedente
  const datiMomento =
    dati && (momento === "adesso" ? dati.time !== null : dati.time === null) ? dati : null;
  const inCaricamento = caricamento || (!!dati && !datiMomento);

  // gruppi che si possono guardare: tutti quelli della risposta con il permesso, altrimenti i propri
  const gruppi = useMemo(() => {
    const tutti = dati ? dati.groups : [];
    return cambiaGruppo ? tutti : tutti.filter(g => gruppiUtente.indexOf(String(g.id)) >= 0);
  }, [dati, cambiaGruppo, gruppiUtente]);
  const gruppoValido =
    gruppo === "tutti" || gruppi.some(g => String(g.id) === gruppo) ? gruppo : "tutti";

  const vista = useMemo(() => {
    const permessi = gruppi.map(g => String(g.id));
    const scelti = (datiMomento && !inCaricamento ? datiMomento.groups : []).filter(g =>
      gruppoValido === "tutti"
        ? permessi.indexOf(String(g.id)) >= 0
        : String(g.id) === gruppoValido,
    );
    const reparti: Record<
      string,
      { nome: string; membri: Set<number> | null; persone: Map<number, TChiCePersona> }
    > = {};
    const tuttiMembri = new Set<number>();
    let membriNoti = true;
    scelti.forEach(g =>
      g.departments.forEach(d => {
        const chiave = d.id === null ? "nessuno" : String(d.id);
        if (!reparti[chiave]) {
          reparti[chiave] = {
            nome: d.name || "Senza reparto",
            membri: new Set<number>(),
            persone: new Map(),
          };
        }
        const r = reparti[chiave];
        if (d.member_ids && r.membri) {
          d.member_ids.forEach(id => {
            r.membri && r.membri.add(id);
            tuttiMembri.add(id);
          });
        } else {
          r.membri = null;
          membriNoti = false;
        }
        // una persona in più gruppi compare una volta sola
        d.employees.forEach(e => {
          if (!r.persone.has(e.id)) r.persone.set(e.id, e);
        });
      }),
    );

    const elenco: TRepartoVista[] = Object.keys(reparti).map(chiave => {
      const r = reparti[chiave];
      return {
        chiave,
        nome: r.nome,
        membri: r.membri ? r.membri.size : null,
        persone: Array.from(r.persone.values())
          .map(p => personaVista(p, momento, adesso, tipoStraordinarioId))
          .sort(confrontaPersone),
      };
    });

    const persone = new Map<number, TPersonaVista>();
    elenco.forEach(r => r.persone.forEach(p => persone.set(p.id, p)));
    const tutte = Array.from(persone.values());

    return {
      reparti: elenco.sort((a, b) => a.nome.localeCompare(b.nome, "it", { sensitivity: "base" })),
      conteggi: {
        presenti: tutte.length,
        inServizio: tutte.filter(p => p.inServizio).length,
        straordinario: tutte.filter(p => p.straordinario).length,
        anomalie: tutte.filter(p => !!p.anomalia).length,
        membri: membriNoti ? tuttiMembri.size : null,
      },
    };
  }, [datiMomento, inCaricamento, gruppi, gruppoValido, momento, adesso, tipoStraordinarioId]);

  const parole = normalizza(ricerca).split(/\s+/).filter(Boolean);
  const corrisponde = (p: TPersonaVista): boolean => {
    if (filtro === "straordinario" && !p.straordinario) return false;
    if (filtro === "anomalie" && !p.anomalia) return false;
    if (!parole.length) return true;
    const nome = normalizza(`${p.cognome} ${p.nome}`);
    return parole.every(w => nome.indexOf(w) >= 0);
  };
  const filtrando = parole.length > 0 || filtro !== "tutti";
  const reparti = vista.reparti
    .map(r => ({ ...r, visibili: r.persone.filter(corrisponde) }))
    .filter(r => !filtrando || r.visibili.length > 0)
    // prima i reparti con qualcuno, poi quelli vuoti
    .sort((a, b) => (a.persone.length > 0 ? 0 : 1) - (b.persone.length > 0 ? 0 : 1));

  const { conteggi } = vista;
  // "Adesso": fuori chi non ha una timbratura aperta; "Oggi" e "Domani": chi non ha turni
  const senza =
    conteggi.membri !== null
      ? Math.max(
          0,
          conteggi.membri - (momento === "adesso" ? conteggi.inServizio : conteggi.presenti),
        )
      : null;
  const titolo =
    momento === "adesso"
      ? "Chi c'è adesso"
      : momento === "oggi"
      ? "Chi è in turno oggi"
      : "Chi è in turno domani";
  const vuotoReparto =
    momento === "adesso"
      ? "Nessuno in servizio adesso"
      : momento === "oggi"
      ? "Nessuno in turno oggi"
      : "Nessuno in turno domani";

  const totali: JSX.Element[] = [];
  const totale = (
    chiave: string,
    valore: number,
    testo: string,
    colore: string,
    filtroTotale?: "straordinario" | "anomalie",
  ): void => {
    totali.push(
      filtroTotale ? (
        <button
          key={chiave}
          type="button"
          className={`totale ${colore}`}
          aria-pressed={filtro === filtroTotale}
          onClick={(): void => setFiltro(filtro === filtroTotale ? "tutti" : filtroTotale)}
          title={filtro === filtroTotale ? "Mostra tutti" : `Mostra solo: ${testo}`}
        >
          <b className="numeri">{valore}</b>
          <span>{testo}</span>
        </button>
      ) : (
        <div key={chiave} className={`totale ${colore}`}>
          <b className="numeri">{valore}</b>
          <span>{testo}</span>
        </div>
      ),
    );
  };
  if (momento === "adesso") {
    totale("in", conteggi.inServizio, "in servizio", "verde");
    totale("str", conteggi.straordinario, "in straordinario", "viola", "straordinario");
    totale("ano", conteggi.anomalie, "da controllare", "ambra", "anomalie");
    if (senza !== null) totale("fuori", senza, "non in servizio", "grigio");
  } else {
    totale(
      "in",
      conteggi.presenti,
      momento === "oggi" ? "in turno oggi" : "in turno domani",
      "blu",
    );
    if (momento === "oggi") {
      totale("ser", conteggi.inServizio, "in servizio adesso", "verde");
      totale("ano", conteggi.anomalie, "da controllare", "ambra", "anomalie");
    }
    if (senza !== null) totale("fuori", senza, "senza turno", "grigio");
  }

  const opzioniGruppi = [
    { value: "tutti", label: cambiaGruppo ? "Tutti i gruppi" : "Tutti i miei gruppi" },
    ...gruppi.map(g => ({ value: String(g.id), label: g.name })),
  ];

  return (
    <section className="scheda" aria-labelledby="titolo-chi-ce">
      <div className="testa">
        <div className="accanto">
          <h2 id="titolo-chi-ce">{titolo}</h2>
          {dati && gruppi.length === 1 && <span className="attenuato">{gruppi[0].name}</span>}
        </div>
        <span className="stato-lettura">
          {aggiornamento && <Spin size="small" />}
          <Button size="small" icon={<ReloadOutlined />} aria-label="Aggiorna" onClick={ricarica} />
          {momento === "adesso"
            ? `Aggiornato alle ${adesso.substr(11, 5)}, ogni minuto`
            : moment(dati?.date || undefined).format("dddd D MMMM")}
        </span>
      </div>

      <div className="filtri">
        <Segmented
          aria-label="Momento"
          value={momento}
          options={[
            { label: "Adesso", value: "adesso" },
            { label: "Oggi", value: "oggi" },
            { label: "Domani", value: "domani" },
          ]}
          onChange={(v): void => {
            setFiltro("tutti");
            onMomento(v as TMomento);
          }}
        />
        {gruppi.length > 1 && (
          <Select
            className="gruppo"
            aria-label="Gruppo"
            value={gruppoValido}
            options={opzioniGruppi}
            showSearch
            optionFilterProp="label"
            onChange={(v: string): void => onGruppo(v)}
          />
        )}
        <Input
          className="ricerca"
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Cerca persona"
          aria-label="Cerca persona"
          value={ricerca}
          onChange={(e): void => setRicerca(e.target.value)}
        />
      </div>

      {errore && (
        <Alert
          type="error"
          showIcon
          message={errore}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={ricarica}>
              Riprova
            </Button>
          }
        />
      )}

      {inCaricamento && (
        <div className="carica-chi-ce" role="status">
          <div className="messaggio">
            <Spin size="small" />
            <span>
              {momento === "adesso"
                ? "Lettura di chi c'è adesso…"
                : momento === "oggi"
                ? "Lettura dei turni di oggi…"
                : "Lettura dei turni di domani…"}
            </span>
          </div>
          <div className="tessere" aria-hidden="true">
            {[0, 1, 2].map(i => (
              <div key={i} className="tessera">
                <Skeleton active avatar={{ size: 30 }} paragraph={{ rows: 4 }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {!inCaricamento && dati && gruppi.length === 0 && (
        <span className="vuoto">
          Nessun gruppo da mostrare: non fai parte di gruppi che puoi vedere.
        </span>
      )}

      {!inCaricamento && datiMomento && gruppi.length > 0 && (
        <>
          <div className="totali">{totali}</div>
          {momento !== "domani" && (
            <div className="legenda" aria-hidden="true">
              {(momento === "adesso"
                ? (["in_corso", "timbrato", "non_timbrato", "straordinario"] as TStatoTurno[])
                : ([
                    "in_corso",
                    "timbrato",
                    "da_svolgere",
                    "non_timbrato",
                    "straordinario",
                  ] as TStatoTurno[])
              ).map(stato => (
                <span key={stato} className={`chip-turno ${stato}`}>
                  <IconaStato
                    stato={stato}
                    viva={
                      stato === "in_corso" || (momento === "adesso" && stato === "straordinario")
                    }
                  />
                  {momento === "adesso" && stato === "timbrato" ? "Già uscito" : DESCRIZIONI[stato]}
                </span>
              ))}
            </div>
          )}

          {reparti.length === 0 ? (
            <span className="vuoto">
              {filtrando ? "Nessuna persona corrisponde alla ricerca." : vuotoReparto}
            </span>
          ) : (
            <div className="tessere">
              {reparti.map(r => {
                const chiuso = chiusi.indexOf(r.chiave) >= 0 && !filtrando;
                const inServizio = r.persone.filter(p => p.inServizio);
                // "Adesso" conta chi è dentro; "Oggi" e "Domani" chi ha un turno
                const contati = momento === "adesso" ? inServizio : r.persone;
                const conStraordinario = contati.filter(p => p.straordinario).length;
                const ordinari = contati.length - conStraordinario;
                const base = r.membri && r.membri > 0 ? r.membri : Math.max(1, contati.length);
                const idElenco = `reparto-${r.chiave}`;
                return (
                  <article
                    key={r.chiave}
                    className={`tessera${r.persone.length === 0 ? " vuota" : ""}${
                      chiuso ? " chiusa" : ""
                    }`}
                  >
                    <h3>
                      <button
                        type="button"
                        className="testa-tessera"
                        aria-expanded={!chiuso}
                        aria-controls={idElenco}
                        onClick={(): void => apriChiudi(r.chiave)}
                        title={chiuso ? `Apri ${r.nome}` : `Chiudi ${r.nome}`}
                      >
                        <DownOutlined className="freccia" aria-hidden="true" />
                        <span className="nome-reparto">{r.nome}</span>
                        <span className="conteggio numeri">
                          {r.membri !== null ? `${contati.length} su ${r.membri}` : contati.length}
                        </span>
                      </button>
                    </h3>
                    <div className="barra" aria-hidden="true">
                      <i style={{ width: `${(ordinari / base) * 100}%`, background: "#2E8A63" }} />
                      <i
                        style={{
                          width: `${(conStraordinario / base) * 100}%`,
                          background: "#6A98B9",
                        }}
                      />
                    </div>
                    <div id={idElenco} hidden={chiuso}>
                      {r.persone.length === 0 ? (
                        <span className="vuoto">{vuotoReparto}</span>
                      ) : (
                        <ElencoScorrevole>
                          {r.visibili.map(p => (
                            <li key={p.id}>
                              <AvatarPersona
                                persona={{ nome: p.nome, cognome: p.cognome, avatar: null }}
                              />
                              <div className="chi">
                                <span className="nome">
                                  {p.cognome} {p.nome}
                                  {String(p.id) === String(utenteId) && (
                                    <span className="pillola grigio piccola">Tu</span>
                                  )}
                                </span>
                                {p.turni.length > 0 && (
                                  <span className="turni-persona">
                                    {p.turni.map(t => (
                                      <React.Fragment key={t.chiave}>
                                        <span
                                          className={`chip-turno ${t.stato}`}
                                          title={t.descrizione}
                                        >
                                          <IconaStato stato={t.stato} viva={t.viva} />
                                          {STATO_DA_LEGGERE[t.stato] && (
                                            <span className="solo-lettori">
                                              {`${STATO_DA_LEGGERE[t.stato]}: `}
                                            </span>
                                          )}
                                          <span className="numeri">{t.testo}</span>
                                          {t.nota && <span className="nota numeri">{t.nota}</span>}
                                        </span>
                                        {t.avviso && (
                                          <span
                                            className="pillola ambra piccola"
                                            title={t.avvisoCompleto || undefined}
                                          >
                                            {t.avviso}
                                          </span>
                                        )}
                                      </React.Fragment>
                                    ))}
                                  </span>
                                )}
                              </div>
                            </li>
                          ))}
                        </ElencoScorrevole>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
};
